/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState
} from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import Paper from "@mui/material/Paper";

import { colors } from "@dotnet/telematics-design-system";

import { ROUTES, TABLE_NAMES } from "../../../../../shared";
import RectangleLoader from "../../../../../shared/components/loaders/RectangleLoader";
import { useStyles } from "../../../styles";
import { DetailsDataType } from "../../../types";
import useDetailsCard from "../hooks/useDetailsCard";

interface IdetailsCardProps {
  children: ReactNode;
  cardTitle: string;
  collapsable?: boolean;
  inlineSubtitle?: string;
  index?: number;
  firstCollapsableCard?: boolean;
  detailsData?: DetailsDataType;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  contentLoading?: boolean;
  moreActions?: ReactNode;
  infoIcon?: boolean;
  infoTooltip?: string | ReactNode;
  showExpandTooltip?: boolean;
  expandByDefault?: boolean;
}

const DetailsCard = ({
  children,
  cardTitle,
  collapsable,
  inlineSubtitle,
  index,
  firstCollapsableCard,
  detailsData,
  setIsLoading,
  contentLoading = false,
  moreActions,
  infoIcon,
  infoTooltip,
  showExpandTooltip = false,
  expandByDefault
}: IdetailsCardProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const { renderExpandIcon, renderTitleArea, renderChildren } = useDetailsCard({
    colors,
    t,
    cardTitle,
    infoIcon,
    infoTooltip,
    inlineSubtitle,
    moreActions,
    detailsData,
    collapsable
  });

  const { loading: isTableLoading } = useTableStoreByRoute(
    location.pathname as ROUTES
  );

  const isDetailsLoading = useDetailsStore(state => state.isLoading);

  const resourceName = location.pathname.replace("/", "") as TABLE_NAMES;

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const {
    detailsPaperCard,
    firstCollapsablePaperCard,
    detailsPaperCardContent,
    paperCardTitle
  } = useStyles();

  const displayLoader = isDetailsLoading || isTableLoading || contentLoading;
  const paperCardCSS = firstCollapsableCard
    ? firstCollapsablePaperCard
    : detailsPaperCard;

  const isFirstItemAllowedToCollapse =
    !displayLoader && (index === 0 || expandByDefault);

  const transitionTiming = "0.2s";

  const collapseState = {
    arrowRotationAngle: isOpen ? 0 : "-0.5turn",
    minCardHeight: collapsable ? "56px" : "172px",
    content: isOpen ? children : null,
    style: {
      maxHeight: isOpen ? "500px" : 0,
      opacity: isOpen ? 1 : 0,
      transition: `all ease-in-out ${transitionTiming}`
    }
  };

  useEffect(() => {
    // reset isOpen state on loader to avoid state cache
    if (displayLoader) setIsOpen(false);

    if (isFirstItemAllowedToCollapse) setIsOpen(true);
    if (setIsLoading) setIsLoading(displayLoader);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, setIsLoading, displayLoader]);

  const handleToggleCollapse = () => setIsOpen(prev => !prev);

  if (displayLoader && collapsable) return null;

  const renderTitleContent = () =>
    displayLoader ? (
      <RectangleLoader
        height="16px"
        width="216px"
        testId={`details-card-title-loader-${cardTitle}`}
      />
    ) : (
      renderTitleArea(
        () =>
          renderExpandIcon({
            handleToggleCollapse,
            isOpen,
            showExpandTooltip,
            transitionTiming,
            arrowRotationAngle: collapseState.arrowRotationAngle
          }),
        resourceName
      )
    );

  const renderCollapseContent = () =>
    collapsable ? (
      <div css={css(collapseState.style)}>{collapseState.content}</div>
    ) : (
      renderChildren(displayLoader, children)
    );

  return (
    <Paper
      data-testid="details-card"
      css={css([
        paperCardCSS,
        {
          border: `1px solid ${colors.gray200}`,
          minHeight: collapseState.minCardHeight
        }
      ])}
    >
      <div css={css(detailsPaperCardContent)}>
        <div css={css(paperCardTitle)}>{renderTitleContent()}</div>
        {renderCollapseContent()}
      </div>
    </Paper>
  );
};

export default DetailsCard;
